import React, { useContext, useEffect, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import { Spinner } from "react-bootstrap";
import { useSyncedField } from "../../components/Sidebar";
import SidebarContext from "../../components/Sidebar/SidebarContext";
import StarekContext from "../../context/StarekContext";

const ENTITY_TYPES_QUERY = gql`
    query EntityTypesForIntegration {
        entityTypes {
            id
            code
            singular
            plural
            type
            is_active
        }
    }
`;

// Same eligibility bit the Webhook feature uses (EntityKind.TYPE_FLAGS.BIT_LOCATION_WEBHOOK) —
// shared "eligible for outbound-call subscription" flag for both features.
const SUBSCRIBABLE_BIT = 5;
const ACTION_BITS = { create: 0, update: 1, delete: 2 };
const ACTIONS = ["create", "update", "delete"];

function isBitSet(value, bit) {
    return (value & (1 << bit)) !== 0;
}

function buildSubscriptionsFromMatrix(matrix) {
    const result = [];
    for (const [entityTypeId, actions] of Object.entries(matrix)) {
        let mask = 0;
        if (actions.create) mask |= 1 << ACTION_BITS.create;
        if (actions.update) mask |= 1 << ACTION_BITS.update;
        if (actions.delete) mask |= 1 << ACTION_BITS.delete;
        if (mask > 0) {
            result.push({ entity_type_id: Number(entityTypeId), action_mask: mask });
        }
    }
    return result;
}

function buildMatrixFromSubscriptions(subscriptions, entityTypes) {
    const matrix = {};
    for (const et of entityTypes) {
        matrix[et.id] = { create: false, update: false, delete: false };
    }
    if (!subscriptions) return matrix;
    for (const sub of subscriptions) {
        if (matrix[sub.entity_type_id]) {
            matrix[sub.entity_type_id] = {
                create: isBitSet(sub.action_mask, ACTION_BITS.create),
                update: isBitSet(sub.action_mask, ACTION_BITS.update),
                delete: isBitSet(sub.action_mask, ACTION_BITS.delete),
            };
        }
    }
    return matrix;
}

function ToggleSwitch({ id, checked, onChange }) {
    return (
        <div className="form-check form-switch mb-0 d-flex justify-content-center">
            <input
                id={id}
                type="checkbox"
                className={`form-check-input ${checked ? "toggle-switch-on" : "toggle-switch-off"}`}
                role="switch"
                checked={checked}
                onChange={onChange}
            />
        </div>
    );
}

function SelectAllButton({ allOn, onClick }) {
    return (
        <button
            type="button"
            className={`select-all-btn btn btn-sm rounded-circle d-inline-flex align-items-center justify-content-center ${allOn ? "btn-primary" : "btn-dark"}`}
            title={allOn ? "Clear all" : "Select all"}
            onClick={onClick}
        >
            <i className="fas fa-check-double font-size-10px" />
        </button>
    );
}

const SectionTitle = ({ children }) => (
    <div className="d-flex align-items-center mt-1 mb-2">
        <h6 className="mb-0 text-uppercase text-muted">{children}</h6>
        <hr className="flex-grow-1 ms-2 my-1" />
    </div>
);

const IntegrationEvents = () => {
    const starekContext = useContext(StarekContext);
    const { entity } = useContext(SidebarContext);
    const [, setSubscriptions] = useSyncedField("subscriptions", []);

    const { data, loading } = useQuery(ENTITY_TYPES_QUERY, {
        client: starekContext.engineClient,
    });

    const subscribableEntityTypes = (data?.entityTypes || [])
        .filter((et) => et.is_active && isBitSet(et.type, SUBSCRIBABLE_BIT))
        .sort((a, b) => (a.singular || a.code).localeCompare(b.singular || b.code));

    const [matrix, setMatrix] = useState({});

    useEffect(() => {
        if (subscribableEntityTypes.length === 0) return;

        // New integrations start with every entity type + action enabled —
        // existing (already-saved) integrations always load their actual
        // saved subscriptions, even if that happens to be zero.
        if (!entity?.id) {
            const allOn = {};
            for (const et of subscribableEntityTypes) {
                allOn[et.id] = { create: true, update: true, delete: true };
            }
            setMatrix(allOn);
            return;
        }

        const existing = entity?.subscriptions || [];
        setMatrix(buildMatrixFromSubscriptions(existing, subscribableEntityTypes));
    }, [entity?.id, subscribableEntityTypes.length]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setSubscriptions(buildSubscriptionsFromMatrix(matrix));
    }, [matrix]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggle = (entityTypeId, action) => {
        setMatrix((prev) => ({
            ...prev,
            [entityTypeId]: {
                ...prev[entityTypeId],
                [action]: !prev[entityTypeId]?.[action],
            },
        }));
    };

    const toggleAllForRow = (entityTypeId) => {
        const current = matrix[entityTypeId] || {};
        const allOn = ACTIONS.every((a) => current[a]);
        setMatrix((prev) => ({
            ...prev,
            [entityTypeId]: Object.fromEntries(ACTIONS.map((a) => [a, !allOn])),
        }));
    };

    const toggleAllForAction = (action) => {
        const allOn = subscribableEntityTypes.every((et) => matrix[et.id]?.[action]);
        setMatrix((prev) => {
            const next = { ...prev };
            for (const et of subscribableEntityTypes) {
                next[et.id] = { ...next[et.id], [action]: !allOn };
            }
            return next;
        });
    };

    const setAll = (value) => {
        const next = {};
        for (const et of subscribableEntityTypes) {
            next[et.id] = { create: value, update: value, delete: value };
        }
        setMatrix(next);
    };

    if (loading) {
        return (
            <div className="p-3 text-center">
                <Spinner animation="border" size="sm" />
            </div>
        );
    }

    return (
        <div className="p-3">
            <SectionTitle>Event Subscriptions</SectionTitle>
            <p className="text-muted small mb-2">
                Select which entity types and actions should trigger this integration.
            </p>
            <div className="d-flex gap-2 mb-3">
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setAll(true)}>
                    Everything
                </button>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setAll(false)}>
                    None
                </button>
            </div>
            <table className="event-table">
                <colgroup>
                    <col className="resource" />
                    <col className="action" />
                    <col className="action" />
                    <col className="action" />
                    <col className="row-action" />
                </colgroup>
                <thead>
                    <tr>
                        <th>Entity</th>
                        {ACTIONS.map((action) => {
                            const allOn = subscribableEntityTypes.length > 0 &&
                                subscribableEntityTypes.every((et) => matrix[et.id]?.[action]);
                            return (
                                <th key={action} className="action">
                                    <div className="d-flex flex-column align-items-center gap-1">
                                        <span className="text-capitalize">{action}</span>
                                        <SelectAllButton
                                            allOn={allOn}
                                            onClick={() => toggleAllForAction(action)}
                                        />
                                    </div>
                                </th>
                            );
                        })}
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {subscribableEntityTypes.map((et) => {
                        const allOn = ACTIONS.every((a) => matrix[et.id]?.[a]);
                        return (
                            <tr key={et.id}>
                                <td>{et.singular || et.plural || et.code}</td>
                                {ACTIONS.map((action) => (
                                    <td key={action} className="action">
                                        <ToggleSwitch
                                            id={`${et.id}-${action}`}
                                            checked={!!matrix[et.id]?.[action]}
                                            onChange={() => toggle(et.id, action)}
                                        />
                                    </td>
                                ))}
                                <td className="row-action">
                                    <SelectAllButton
                                        allOn={allOn}
                                        onClick={() => toggleAllForRow(et.id)}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                    {subscribableEntityTypes.length === 0 && (
                        <tr>
                            <td colSpan={5} className="text-muted text-center py-3">
                                No integration-enabled entity types found.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default IntegrationEvents;
